import type {
  ButtonHTMLAttributes,
  InputHTMLAttributes,
  KeyboardEvent as ReactKeyboardEvent,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from "react";
import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  AlertCircle,
  CheckCircle2,
  ChevronDown,
  Info,
  LoaderCircle,
  X,
  XCircle,
} from "lucide-react";
import type { RequestStatus, Shift } from "../types";
import { SHIFT_LABELS, STATUS_LABELS } from "../utils";
import { lockBodyScroll } from "../dialog-utils";

export function Button({
  className = "",
  variant = "primary",
  loading,
  children,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: "primary" | "secondary" | "ghost" | "danger";
  loading?: boolean;
}) {
  return (
    <button
      {...props}
      className={`button button--${variant} ${className}`}
      disabled={loading || props.disabled}
      aria-busy={loading || undefined}
    >
      {loading ? (
        <LoaderCircle className="spin" aria-hidden="true" size={18} />
      ) : null}
      {children}
    </button>
  );
}

export function Field({
  label,
  hint,
  error,
  id,
  ...props
}: InputHTMLAttributes<HTMLInputElement> & {
  label: string;
  hint?: ReactNode;
  error?: string;
}) {
  const inputId = id ?? props.name;
  const descriptionId = error ? `${inputId}-error` : hint ? `${inputId}-hint` : undefined;
  return (
    <label className="field" htmlFor={inputId}>
      <span className="field__label">{label}</span>
      <input
        id={inputId}
        className={error ? "input input--error" : "input"}
        aria-invalid={Boolean(error)}
        aria-describedby={descriptionId}
        {...props}
      />
      {hint && !error ? <span className="field__hint" id={`${inputId}-hint`}>{hint}</span> : null}
      {error ? (
        <span className="field__error" id={`${inputId}-error`}>
          {error}
        </span>
      ) : null}
    </label>
  );
}

export function SelectField({
  label,
  hint,
  error,
  id,
  children,
  ...props
}: SelectHTMLAttributes<HTMLSelectElement> & {
  label: string;
  hint?: ReactNode;
  error?: string;
}) {
  const inputId = id ?? props.name;
  const descriptionId = error ? `${inputId}-error` : hint ? `${inputId}-hint` : undefined;
  return (
    <label className="field" htmlFor={inputId}>
      <span className="field__label">{label}</span>
      <select
        id={inputId}
        className={error ? "input input--error" : "input"}
        aria-invalid={Boolean(error)}
        aria-describedby={descriptionId}
        {...props}
      >
        {children}
      </select>
      {hint && !error ? <span className="field__hint" id={`${inputId}-hint`}>{hint}</span> : null}
      {error ? <span className="field__error" id={`${inputId}-error`}>{error}</span> : null}
    </label>
  );
}

export interface SearchableOption {
  value: string;
  label: string;
}

type SearchableComboboxProps = {
  label: string;
  options: readonly SearchableOption[];
  value: string | string[];
  onChange: (value: string | string[]) => void;
  multiple?: boolean;
  disabled?: boolean;
  required?: boolean;
  placeholder?: string;
  hint?: ReactNode;
  error?: string;
  id?: string;
};

function searchableKey(value: string) {
  return value
    .toLocaleLowerCase("tr-TR")
    .replaceAll("ı", "i")
    .normalize("NFD")
    .replace(/\p{M}/gu, "");
}

export function SearchableCombobox({
  label,
  options,
  value,
  onChange,
  multiple = false,
  disabled = false,
  required = false,
  placeholder = "Seçin veya arayın",
  hint,
  error,
  id,
}: SearchableComboboxProps) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const listId = `${inputId}-options`;
  const selectedValues = Array.isArray(value) ? value : value ? [value] : [];
  const selectedOption = multiple ? null : options.find((option) => option.value === selectedValues[0]);
  const [query, setQuery] = useState(selectedOption?.label ?? "");
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const skipFocusOpenRef = useRef(false);
  const keyboardNavigationRef = useRef(false);
  const [placement, setPlacement] = useState<{ top?: number; bottom?: number; maxHeight: number }>({ top: 0, maxHeight: 304 });
  const touchGestureRef = useRef<{ pointerId: number; startY: number; moved: boolean } | null>(null);
  const suppressTouchClickRef = useRef(false);

  useEffect(() => {
    if (!multiple && !open) setQuery(selectedOption?.label ?? "");
  }, [multiple, open, selectedOption?.label]);

  const filtered = useMemo(() => {
    const selectedLabel = selectedOption?.label ?? "";
    const term = searchableKey(query === selectedLabel ? "" : query.trim());
    if (!term) return options;
    return options
      .map((option, order) => {
        const label = searchableKey(option.label);
        return { option, order, rank: label.startsWith(term) ? 0 : label.includes(term) ? 1 : 2 };
      })
      .filter((item) => item.rank < 2)
      .sort((left, right) => left.rank - right.rank
        || left.option.label.localeCompare(right.option.label, "tr-TR", { sensitivity: "base", numeric: true })
        || left.order - right.order)
      .map((item) => item.option);
  }, [options, query, selectedOption?.label]);

  useEffect(() => setActiveIndex(-1), [query, open]);

  useEffect(() => {
    if (!open) return;
    const outside = (event: PointerEvent) => {
      if (event.target instanceof Node && !rootRef.current?.contains(event.target)) {
        setOpen(false);
        setQuery(selectedOption?.label ?? "");
      }
    };
    document.addEventListener("pointerdown", outside, true);
    return () => document.removeEventListener("pointerdown", outside, true);
  }, [open, selectedOption?.label]);

  useLayoutEffect(() => {
    if (!open) return;
    let frame = 0;
    const measure = () => {
      const root = rootRef.current;
      const input = inputRef.current;
      if (!root || !input) return;
      const rootRect = root.getBoundingClientRect();
      const anchor = input.getBoundingClientRect();
      const viewport = window.visualViewport;
      let top = (viewport?.offsetTop ?? 0) + 8;
      let bottom = (viewport?.offsetTop ?? 0) + (viewport?.height ?? window.innerHeight) - 8;
      for (let parent = root.parentElement; parent && parent !== document.body; parent = parent.parentElement) {
        if (/(auto|scroll|hidden|clip)/.test(getComputedStyle(parent).overflowY)) {
          const rect = parent.getBoundingClientRect();
          top = Math.max(top, rect.top + 6);
          bottom = Math.min(bottom, rect.bottom - 6);
        }
      }
      const below = Math.max(0, bottom - anchor.bottom - 6);
      const above = Math.max(0, anchor.top - top - 6);
      const upwards = below < 180 && above > below;
      const next = {
        top: upwards ? undefined : anchor.bottom - rootRect.top + 6,
        bottom: upwards ? rootRect.bottom - anchor.top + 6 : undefined,
        maxHeight: Math.min(304, upwards ? above : below),
      };
      setPlacement((current) => current.top === next.top && current.bottom === next.bottom && current.maxHeight === next.maxHeight ? current : next);
    };
    const schedule = () => { cancelAnimationFrame(frame); frame = requestAnimationFrame(measure); };
    measure();
    document.addEventListener("scroll", schedule, true);
    window.addEventListener("resize", schedule);
    window.visualViewport?.addEventListener("resize", schedule);
    window.visualViewport?.addEventListener("scroll", schedule);
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(schedule);
    if (rootRef.current) observer?.observe(rootRef.current);
    return () => {
      cancelAnimationFrame(frame);
      observer?.disconnect();
      document.removeEventListener("scroll", schedule, true);
      window.removeEventListener("resize", schedule);
      window.visualViewport?.removeEventListener("resize", schedule);
      window.visualViewport?.removeEventListener("scroll", schedule);
    };
  }, [open, selectedValues.length]);

  useLayoutEffect(() => {
    if (!open || activeIndex < 0 || !keyboardNavigationRef.current) return;
    const option = document.getElementById(`${inputId}-option-${activeIndex}`);
    const list = option?.parentElement;
    if (!option || !list) return;
    const itemRect = option.getBoundingClientRect();
    const listRect = list.getBoundingClientRect();
    if (itemRect.top < listRect.top) list.scrollTop -= listRect.top - itemRect.top;
    else if (itemRect.bottom > listRect.bottom) list.scrollTop += itemRect.bottom - listRect.bottom;
  }, [open, activeIndex, inputId, placement.maxHeight]);

  function focusInput() {
    if (inputRef.current && document.activeElement !== inputRef.current) {
      skipFocusOpenRef.current = true;
      inputRef.current.focus({ preventScroll: true });
    }
  }

  function closeList() {
    setOpen(false);
    setQuery(selectedOption?.label ?? "");
    focusInput();
  }

  function select(option: SearchableOption) {
    if (multiple) {
      const next = selectedValues.includes(option.value)
        ? selectedValues.filter((item) => item !== option.value)
        : [...selectedValues, option.value];
      onChange(next);
      setQuery("");
      setOpen(true);
    } else {
      onChange(option.value);
      setQuery(option.label);
      setOpen(false);
    }
    focusInput();
  }

  function clear() {
    onChange(multiple ? [] : "");
    setQuery("");
    setOpen(false);
  }

  function onKeyDown(event: ReactKeyboardEvent<HTMLInputElement>) {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      keyboardNavigationRef.current = true;
      setOpen(true);
      const direction = event.key === "ArrowDown" ? 1 : -1;
      setActiveIndex((current) => filtered.length
        ? current < 0 ? (direction > 0 ? 0 : filtered.length - 1) : (current + direction + filtered.length) % filtered.length
        : -1);
    } else if (event.key === "Enter" && open && filtered[activeIndex]) {
      event.preventDefault();
      select(filtered[activeIndex]);
    } else if (event.key === "Escape" && open) {
      event.preventDefault();
      event.stopPropagation();
      closeList();
    } else if (event.key === "Tab") {
      setOpen(false);
      setQuery(selectedOption?.label ?? "");
    } else if (event.key === "Backspace" && multiple && !query && selectedValues.length) {
      onChange(selectedValues.slice(0, -1));
    }
  }

  return (
    <div className="field searchable-combobox" ref={rootRef}>
      <label className="field__label" htmlFor={inputId}>{label}</label>
      {multiple && selectedValues.length ? (
        <div className="searchable-combobox__chips">
          {selectedValues.map((selected) => {
            const option = options.find((item) => item.value === selected);
            return option ? <button type="button" key={selected} onClick={() => onChange(selectedValues.filter((item) => item !== selected))}>{option.label}<X size={14} aria-hidden="true" /></button> : null;
          })}
        </div>
      ) : null}
      <div className="searchable-combobox__control">
        <input
          ref={inputRef}
          id={inputId}
          className={error ? "input input--error" : "input"}
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={open}
          aria-controls={listId}
          aria-activedescendant={open && filtered[activeIndex] ? `${inputId}-option-${activeIndex}` : undefined}
          aria-invalid={Boolean(error)}
          autoComplete="off"
          disabled={disabled}
          required={required && !selectedValues.length}
          placeholder={placeholder}
          value={query}
          onFocus={(event) => {
            if (!skipFocusOpenRef.current) setOpen(true);
            skipFocusOpenRef.current = false;
            event.currentTarget.select();
          }}
          onClick={() => setOpen(true)}
          onChange={(event) => { setQuery(event.target.value); setOpen(true); }}
          onKeyDown={onKeyDown}
          onBlur={() => {
            requestAnimationFrame(() => {
              if (!rootRef.current?.contains(document.activeElement)) {
                setOpen(false);
                // The selected value may have been cleared since blur was queued.
                // Single selection text is synchronized by the value effect above.
                if (multiple) setQuery("");
              }
            });
          }}
        />
        {selectedValues.length ? <button type="button" className="searchable-combobox__clear" aria-label={`${label} seçimini temizle`} onClick={clear} disabled={disabled}><X size={16} /></button> : null}
        <button type="button" className="searchable-combobox__toggle" aria-label={`${label} seçeneklerini ${open ? "kapat" : "aç"}`} aria-expanded={open} aria-controls={listId}
          onMouseDown={(event) => event.preventDefault()} disabled={disabled}
          onClick={() => { if (open) closeList(); else { focusInput(); setOpen(true); } }}>
          <ChevronDown size={18} aria-hidden="true" />
        </button>
      </div>
      {!multiple && selectedOption && selectedOption.label.length > 32 ? <span className="searchable-combobox__selected">{selectedOption.label}</span> : null}
      {open && !disabled ? (
        <div className="searchable-combobox__popup" style={placement}>
        <div className="searchable-combobox__options" id={listId} role="listbox" aria-multiselectable={multiple || undefined}>
          {filtered.length ? filtered.map((option, index) => (
            <button
              type="button"
              id={`${inputId}-option-${index}`}
              className={`searchable-combobox__option${index === activeIndex ? " active" : ""}`}
              role="option"
              tabIndex={-1}
              aria-selected={selectedValues.includes(option.value)}
              key={option.value}
              onPointerDown={(event) => {
                if (event.pointerType === "mouse") event.preventDefault();
                else if (event.pointerType === "touch") {
                  touchGestureRef.current = { pointerId: event.pointerId, startY: event.clientY, moved: false };
                  suppressTouchClickRef.current = false;
                }
              }}
              onPointerUp={(event) => {
                const gesture = touchGestureRef.current;
                if (event.pointerType === "touch" && gesture?.pointerId === event.pointerId) {
                  suppressTouchClickRef.current = gesture.moved;
                  touchGestureRef.current = null;
                }
              }}
              onPointerCancel={() => {
                suppressTouchClickRef.current = true;
                touchGestureRef.current = null;
              }}
              onClick={(event) => {
                if (suppressTouchClickRef.current || touchGestureRef.current?.moved) {
                  event.preventDefault();
                  suppressTouchClickRef.current = false;
                  touchGestureRef.current = null;
                  return;
                }
                select(option);
              }}
              onPointerMove={(event) => {
                if (event.pointerType === "mouse") {
                  keyboardNavigationRef.current = false;
                  setActiveIndex(index);
                }
                else if (event.pointerType === "touch" && touchGestureRef.current?.pointerId === event.pointerId
                  && Math.abs(event.clientY - touchGestureRef.current.startY) > 8) {
                  touchGestureRef.current.moved = true;
                }
              }}
            >
              <span>{option.label}</span>{selectedValues.includes(option.value) ? <CheckCircle2 size={16} /> : null}
            </button>
          )) : <span className="searchable-combobox__empty">Eşleşen seçenek yok</span>}
        </div>
        {multiple ? <div className="searchable-combobox__footer"><span>{selectedValues.length} seçili</span><button type="button" onClick={closeList} aria-label="Seçimi tamamla">Tamam</button></div> : null}
        </div>
      ) : null}
      {hint && !error ? <span className="field__hint">{hint}</span> : null}
      {error ? <span className="field__error">{error}</span> : null}
    </div>
  );
}

export function TextareaField({
  label,
  hint,
  error,
  id,
  ...props
}: TextareaHTMLAttributes<HTMLTextAreaElement> & {
  label: string;
  hint?: ReactNode;
  error?: string;
}) {
  const inputId = id ?? props.name;
  const descriptionId = error ? `${inputId}-error` : hint ? `${inputId}-hint` : undefined;
  return (
    <label className="field" htmlFor={inputId}>
      <span className="field__label">{label}</span>
      <textarea
        id={inputId}
        className={error ? "input textarea input--error" : "input textarea"}
        aria-invalid={Boolean(error)}
        aria-describedby={descriptionId}
        {...props}
      />
      {hint && !error ? <span className="field__hint" id={`${inputId}-hint`}>{hint}</span> : null}
      {error ? <span className="field__error" id={`${inputId}-error`}>{error}</span> : null}
    </label>
  );
}

export function Card({
  className = "",
  children,
}: {
  className?: string;
  children: ReactNode;
}) {
  return <section className={`card ${className}`}>{children}</section>;
}

export function Empty({
  icon,
  title,
  text,
}: {
  icon?: ReactNode;
  title: string;
  text: string;
}) {
  return (
    <div className="empty">
      {icon}
      <h3>{title}</h3>
      <p>{text}</p>
    </div>
  );
}

export function Loading({ label = "Yükleniyor" }: { label?: string }) {
  return (
    <div className="loading" role="status" aria-live="polite">
      <LoaderCircle className="spin" aria-hidden="true" />
      <span>{label}</span>
    </div>
  );
}

export function Alert({
  tone = "info",
  children,
  title,
}: {
  tone?: "info" | "success" | "warning" | "danger";
  title?: string;
  children: ReactNode;
}) {
  const Icon =
    tone === "success"
      ? CheckCircle2
      : tone === "danger"
        ? XCircle
        : tone === "warning"
          ? AlertCircle
          : Info;
  return (
    <div
      className={`alert alert--${tone}`}
      role={tone === "danger" ? "alert" : "status"}
    >
      <Icon size={20} aria-hidden="true" />
      <div>
        {title ? <strong>{title}</strong> : null}
        <span>{children}</span>
      </div>
    </div>
  );
}

export function StatusBadge({ status }: { status: RequestStatus }) {
  return (
    <span className={`status status--${status.toLowerCase()}`}>
      {STATUS_LABELS[status]}
    </span>
  );
}

export function ShiftBadge({ shift }: { shift: Shift }) {
  return (
    <span className={`shift shift--${shift.toLowerCase()}`}>
      {SHIFT_LABELS[shift]}
    </span>
  );
}

export function Modal({
  open,
  onClose,
  title,
  children,
  wide = false,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  wide?: boolean;
}) {
  const dialogRef = useRef<HTMLElement>(null);
  const closeRef = useRef(onClose);
  const titleId = useId();
  useEffect(() => {
    closeRef.current = onClose;
  }, [onClose]);
  useEffect(() => {
    if (!open) return;
    const previous = document.activeElement as HTMLElement | null;
    const releaseBodyLock = lockBodyScroll();
    const focusTimer = window.setTimeout(() => dialogRef.current?.focus({ preventScroll: true }), 0);
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented) return;
      const dialogs = [...document.querySelectorAll<HTMLElement>('[role="dialog"][aria-modal="true"]')];
      if (dialogs.at(-1) !== dialogRef.current) return;
      if (event.key === "Escape") {
        event.preventDefault();
        closeRef.current();
        return;
      }
      if (event.key !== "Tab" || !dialogRef.current) return;
      const focusable = [...dialogRef.current.querySelectorAll<HTMLElement>(
        'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [href], [tabindex]:not([tabindex="-1"])',
      )].filter((element) => element.tabIndex >= 0 && element.getClientRects().length > 0 && !element.closest("[hidden],[inert]"));
      const first = focusable[0];
      const last = focusable.at(-1);
      if (!first || !last) {
        event.preventDefault();
        dialogRef.current.focus({ preventScroll: true });
        return;
      }
      const outside = !dialogRef.current.contains(document.activeElement);
      if (event.shiftKey && (document.activeElement === first || document.activeElement === dialogRef.current || outside)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (document.activeElement === last || outside)) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      window.clearTimeout(focusTimer);
      document.removeEventListener("keydown", onKeyDown);
      releaseBodyLock();
      if (previous?.isConnected) previous.focus({ preventScroll: true });
    };
  }, [open]);
  if (!open) return null;
  return (
    <div
      className="modal-backdrop"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <section
        ref={dialogRef}
        className={`modal ${wide ? "modal--wide" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
      >
        <header className="modal__header">
          <h2 id={titleId}>{title}</h2>
          <button
            type="button"
            className="icon-button"
            aria-label="Kapat"
            onClick={onClose}
          >
            <X />
          </button>
        </header>
        <div className="modal__body">{children}</div>
      </section>
    </div>
  );
}

export function Toast({
  message,
  tone = "success",
  onClose,
}: {
  message: string;
  tone?: "success" | "danger" | "info";
  onClose: () => void;
}) {
  const Icon =
    tone === "success" ? CheckCircle2 : tone === "danger" ? XCircle : Info;
  return (
    <div className={`toast toast--${tone}`} role="status" aria-live="polite">
      <Icon size={20} />
      <span>{message}</span>
      <button type="button" aria-label="Bildirimi kapat" onClick={onClose}>
        <X size={18} />
      </button>
    </div>
  );
}

export function PageHeader({
  eyebrow,
  title,
  description,
  actions,
}: {
  eyebrow?: string;
  title: string;
  description?: string;
  actions?: ReactNode;
}) {
  return (
    <header className="page-header">
      <div>
        {eyebrow ? <span className="eyebrow">{eyebrow}</span> : null}
        <h1>{title}</h1>
        {description ? <p>{description}</p> : null}
      </div>
      {actions ? <div className="page-header__actions">{actions}</div> : null}
    </header>
  );
}

export function SkeletonCards({ count = 3 }: { count?: number }) {
  return (
    <div className="cards-grid" aria-label="İçerik yükleniyor">
      {Array.from({ length: count }, (_, index) => (
        <div className="card skeleton-card" key={index}>
          <span />
          <span />
          <span />
        </div>
      ))}
    </div>
  );
}
